import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { onboardingService } from "@/services";
import type {
  AvailabilitySlot,
  CreateLeadPayload,
} from "@/domain/configurator";

export interface ScheduleMeetingPayload {
  slot: AvailabilitySlot;
  packageId?: string;
  packageName?: string;
}

interface ScheduledMeetingsContextType {
  slots: AvailabilitySlot[];
  isLoadingSlots: boolean;
  slotsError: string | null;
  isSubmitting: boolean;
  submitError: string | null;
  fetchAvailabilitySlots: () => Promise<void>;
  createLead: (
    payload: CreateLeadPayload
  ) => Promise<{ success: boolean; leadId: string }>;
  scheduleMeeting: (
    payload: ScheduleMeetingPayload
  ) => Promise<{ success: boolean; meetingId: string }>;
}

const ScheduledMeetingsContext =
  createContext<ScheduledMeetingsContextType | null>(null);

export function ScheduledMeetingsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const fetchAvailabilitySlots = useCallback(async () => {
    setIsLoadingSlots(true);
    setSlotsError(null);
    try {
      const data = await onboardingService.getAvailabilitySlots();
      setSlots(data.slots);
    } catch (err) {
      setSlotsError(
        err instanceof Error ? err.message : "Failed to fetch availability"
      );
    } finally {
      setIsLoadingSlots(false);
    }
  }, []);

  const createLead = useCallback(async (payload: CreateLeadPayload) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await onboardingService.createLead(payload);
      return result;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to submit request";
      setSubmitError(errorMessage);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const scheduleMeeting = useCallback(
    async (payload: ScheduleMeetingPayload) => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        const result = await onboardingService.scheduleMeeting(payload);
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to schedule meeting";
        setSubmitError(errorMessage);
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    []
  );

  return (
    <ScheduledMeetingsContext.Provider
      value={{
        slots,
        isLoadingSlots,
        slotsError,
        isSubmitting,
        submitError,
        fetchAvailabilitySlots,
        createLead,
        scheduleMeeting,
      }}
    >
      {children}
    </ScheduledMeetingsContext.Provider>
  );
}

export function useScheduledMeetings() {
  const context = useContext(ScheduledMeetingsContext);
  if (!context) {
    throw new Error(
      "useScheduledMeetings must be used within a ScheduledMeetingsProvider"
    );
  }
  return context;
}
